import type { SymbolSearchResult } from '@shared/types/symbol'

import { SearchResultRow } from './SearchResult'

import type { SearchGroups } from '@/services/api'

interface SearchResultsProps {
  groups: SearchGroups
  labels: Record<string, string>
  query: string
  activeIndex: number
  flatResults: SymbolSearchResult[]
  onSelect: (result: SymbolSearchResult) => void
  onHoverIndex: (i: number) => void
  isLoading: boolean
}

export function SearchResults({
  groups,
  labels,
  query,
  activeIndex,
  flatResults,
  onSelect,
  onHoverIndex,
  isLoading,
}: SearchResultsProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-sm text-tv-text-secondary">
        Searching…
      </div>
    )
  }

  if (query.trim() && flatResults.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-tv-text-secondary">
        No results for <span className="text-tv-text-primary">&ldquo;{query}&rdquo;</span>
      </div>
    )
  }

  if (!query.trim()) {
    return (
      <div className="flex items-center justify-center py-8 text-sm text-tv-text-muted">
        Type to search symbols, indices, or crypto
      </div>
    )
  }

  let flatIdx = 0

  return (
    <div role="listbox" aria-label="Search results" className="max-h-[420px] overflow-y-auto py-1">
      {Object.entries(groups).map(([cls, items]) => (
        <div key={cls}>
          {/* Group header */}
          <div className="sticky top-0 bg-tv-bg-secondary px-4 py-1.5 text-2xs font-semibold uppercase tracking-wider text-tv-text-muted">
            {labels[cls] ?? cls}
          </div>

          {items.map((item) => {
            const idx = flatIdx++
            return (
              <SearchResultRow
                key={`${item.asset_class}-${item.ticker}`}
                id={`search-result-${idx}`}
                result={item}
                isActive={activeIndex === idx}
                onSelect={onSelect}
                onHover={() => onHoverIndex(idx)}
              />
            )
          })}
        </div>
      ))}
    </div>
  )
}
